import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ComboboxOption } from './types';

/** Debounce applied to `onSearch` when the caller doesn't pick one via `filterDebounceMs`. */
export const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Resolves an option by value, falling back to any option this combobox has shown before.
 *
 * In async mode `options` only holds the latest server results, so a value picked earlier is
 * usually gone by the time its chip or input label needs rendering. Remembering every option
 * we've displayed keeps those labels resolvable for the life of the component.
 */
export function useRememberedOptions(options: ComboboxOption[]) {
  const seen = useRef<Map<string, ComboboxOption>>(new Map<string, ComboboxOption>());

  // Rebuilt whenever `options` changes, so a freshly loaded option overwrites its stale copy.
  const byValue = useMemo(() => {
    for (const option of options) {
      seen.current.set(option.value, option);
    }
    return new Map(seen.current);
  }, [options]);

  return useCallback((value: string) => byValue.get(value), [byValue]);
}

/**
 * Pushes the debounced query to `onSearch`. Held in a ref so an inline callback doesn't
 * re-trigger the search on every render.
 */
export function useSearchNotifier(query: string, onSearch?: (query: string) => void) {
  const latest = useRef(onSearch);
  latest.current = onSearch;

  useEffect(() => {
    latest.current?.(query);
  }, [query]);
}
